import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    ClipboardCheck, FileText, Sparkles, Loader2, RefreshCw, Image as ImageIcon,
    CheckCircle2, XCircle, Calendar, Building2, Eye, Mail, Search,
    Check, X, Minus, ChevronDown, UserCircle2, ListChecks, Copy, MessageCircle, MapPin,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Read-only render of a saved checklist against the template
function ChecklistView({ template, checklist }) {
    const icon = (st) => {
        if (st === "ok") return <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />;
        if (st === "fail") return <X className="w-3.5 h-3.5 text-red-600 shrink-0" />;
        if (st === "na") return <Minus className="w-3.5 h-3.5 text-gray-400 shrink-0" />;
        return <span className="text-[10px] text-muted-foreground/60 w-3.5 text-center shrink-0">·</span>;
    };
    return (
        <div className="space-y-3">
            {template.map((sec) => (
                <div key={sec.section}>
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--trulyn-navy))] mb-1">{sec.title}</div>
                    {sec.type === "items" ? (
                        <div className="rounded-lg border divide-y">
                            {sec.items.map((it) => {
                                const ans = checklist[it.key] || {};
                                return (
                                    <div key={it.key} className="px-2.5 py-1.5">
                                        <div className="flex items-start gap-2 text-xs">
                                            {icon(ans.status)}
                                            <span className="flex-1 leading-tight">{it.label}</span>
                                        </div>
                                        {ans.status === "fail" && ans.comment && (
                                            <div className="ml-5 mt-0.5 text-[11px] text-red-600 italic">{ans.comment}</div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="rounded-lg border divide-y">
                            {sec.steps.map((stp) => (
                                <div key={stp.key} className="px-2.5 py-1.5">
                                    <div className="text-xs font-medium mb-1">{stp.label}</div>
                                    <div className="flex flex-wrap gap-x-4 gap-y-1">
                                        {sec.zones.map((z) => {
                                            const key = `${sec.section}_${stp.key}_${z.key}`;
                                            const ans = checklist[key] || {};
                                            return (
                                                <span key={z.key} className="flex items-center gap-1 text-[11px]">
                                                    {z.label}: {icon(ans.status)}
                                                </span>
                                            );
                                        })}
                                    </div>
                                    {sec.zones.map((z) => {
                                        const ans = checklist[`${sec.section}_${stp.key}_${z.key}`] || {};
                                        if (ans.status !== "fail" || !ans.comment) return null;
                                        return <div key={`c-${z.key}`} className="mt-0.5 text-[11px] text-red-600 italic">{z.label}: {ans.comment}</div>;
                                    })}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export default function AdminAudits() {
    const [audits, setAudits] = useState([]);
    const [auditors, setAuditors] = useState([]);
    const [template, setTemplate] = useState([]);
    const [activeAuditor, setActiveAuditor] = useState(null);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);

    const [selected, setSelected] = useState(null);       // currently expanded audit
    const [photos, setPhotos] = useState([]);
    const [reportStatus, setReportStatus] = useState(null);
    const [reportPdfUrl, setReportPdfUrl] = useState(null);
    const [busy, setBusy] = useState(false);
    const [regenerating, setRegenerating] = useState(false);
    const [processingPhotos, setProcessingPhotos] = useState(false);
    const [emailingReport, setEmailingReport] = useState(false);
    const [serviceNotes, setServiceNotes] = useState({ client_notes: [], tech_notes: [] });
    const [edits, setEdits] = useState({});
    const [savingPhotoId, setSavingPhotoId] = useState(null);

    const editVal = (ph, field) => (edits[ph.id]?.[field] ?? (ph[field] || ""));
    const setEdit = (photoId, field, value) => setEdits((prev) => ({ ...prev, [photoId]: { ...prev[photoId], [field]: value } }));
    const isDirty = (ph) => {
        const e = edits[ph.id];
        if (!e) return false;
        return (("ai_opportunity" in e) && e.ai_opportunity !== (ph.ai_opportunity || "")) ||
               (("ai_corrective_action" in e) && e.ai_corrective_action !== (ph.ai_corrective_action || ""));
    };
    const savePhotoTexts = async (ph) => {
        setSavingPhotoId(ph.id);
        const fields = {
            ai_opportunity: editVal(ph, "ai_opportunity"),
            ai_corrective_action: editVal(ph, "ai_corrective_action"),
        };
        try {
            await api.patch(`/photo-reports/${selected.scheduled_service_id}/photos/${ph.id}`, fields);
            toast.success("Cambios guardados");
            setEdits((prev) => { const n = { ...prev }; delete n[ph.id]; return n; });
            const { data } = await api.get(`/photo-reports/${selected.scheduled_service_id}`);
            if (data) setPhotos(data.photos || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo guardar");
        } finally { setSavingPhotoId(null); }
    };
    const [subTab, setSubTab] = useState("summary");

    const load = async () => {
        setLoading(true);
        try {
            const [aRes, uRes, tRes] = await Promise.all([
                api.get("/audits"),
                api.get("/auth/users").catch(() => ({ data: [] })),
                api.get("/audits/checklist-template").catch(() => ({ data: [] })),
            ]);
            const auds = aRes.data || [];
            setAudits(auds);
            setTemplate(tRes.data || []);
            const map = {};
            (uRes.data || []).filter((u) => u.role === "auditor").forEach((u) => { map[u.id] = u.full_name; });
            auds.forEach((a) => { if (a.auditor_id && !map[a.auditor_id]) map[a.auditor_id] = a.auditor_name || "Auditor"; });
            const list = Object.entries(map).map(([id, name]) => ({ id, name }));
            setAuditors(list);
            setActiveAuditor((prev) => prev || (list[0] ? list[0].id : null));
        } catch { toast.error("Error al cargar auditorías"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const toggle = async (a) => {
        if (selected?.id === a.id) { setSelected(null); return; }
        setSelected(a);
        setPhotos([]); setReportStatus(null); setReportPdfUrl(null);
        setServiceNotes({ client_notes: [], tech_notes: [] });
        const summaryExists = hasChecklist(a) || !!a.executive_summary;
        setSubTab(summaryExists ? "summary" : (hasChecklist(a) ? "checklist" : "photos"));
        api.get(`/schedules/${a.scheduled_service_id}/all-notes`)
            .then(({ data }) => setServiceNotes({ client_notes: data?.client_notes || [], tech_notes: data?.tech_notes || [] }))
            .catch(() => {});
        try {
            const { data } = await api.get(`/photo-reports/${a.scheduled_service_id}`);
            if (data) {
                setPhotos(data.photos || []);
                setReportStatus(data.status || null);
                setReportPdfUrl(data.pdf_url || null);
                // If the audit only has a photo report, default to that tab
                if (!summaryExists && !hasChecklist(a) && (data.photos || []).length) setSubTab("photos");
            }
        } catch { /* no report */ }
    };

    const scorePct = (a) => a.checklist_score?.percent;
    const cleanSummary = (t) => (t || "")
        .replace(/\*\*/g, "")
        .replace(/__/g, "")
        .replace(/^#{1,6}\s*/gm, "")
        .replace(/^\s*[-*]\s+/gm, "• ");
    const copySummary = async (t) => {
        try { await navigator.clipboard.writeText(cleanSummary(t)); toast.success("Resumen copiado"); }
        catch { toast.error("No se pudo copiar"); }
    };
    const scoreColor = (p) => p == null ? "bg-gray-100 text-gray-700 border-gray-200" : p >= 80 ? "bg-emerald-100 text-emerald-800 border-emerald-200" : p >= 60 ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-red-100 text-red-800 border-red-200";
    const hasChecklist = (a) => a.checklist && Object.keys(a.checklist).length > 0;

    const downloadPdf = async (url) => {
        setBusy(true);
        try {
            const res = await api.get(url, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
            window.open(blobUrl, "_blank");
        } catch { toast.error("No se pudo generar el PDF"); }
        finally { setBusy(false); }
    };

    const regenerateSummary = async () => {
        if (!selected) return;
        setRegenerating(true);
        try {
            const { data } = await api.post(`/audits/${selected.id}/executive-summary`);
            setSelected((s) => ({ ...s, executive_summary: data.executive_summary }));
            setAudits((list) => list.map((x) => x.id === selected.id ? { ...x, executive_summary: data.executive_summary } : x));
            toast.success("Resumen actualizado");
        } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el resumen"); }
        finally { setRegenerating(false); }
    };

    const savePhotoField = async (photoId, fields) => {
        setPhotos((ps) => ps.map((p) => p.id === photoId ? { ...p, ...fields } : p));
        try { await api.patch(`/photo-reports/${selected.scheduled_service_id}/photos/${photoId}`, fields); }
        catch { toast.error("No se pudo guardar el texto"); }
    };

    const reloadReport = async () => {
        const { data } = await api.get(`/photo-reports/${selected.scheduled_service_id}`);
        if (data) { setPhotos(data.photos || []); setReportStatus(data.status || null); setReportPdfUrl(data.pdf_url || null); }
        return data;
    };

    const generateAI = async () => {
        if (!selected) return;
        setProcessingPhotos(true);
        setReportStatus("processing");
        try {
            await api.post(`/photo-reports/${selected.scheduled_service_id}/process?force=true`);
            let done = false;
            for (let i = 0; i < 40 && !done; i++) {
                await new Promise((r) => setTimeout(r, 2500));
                const d = await reloadReport();
                if (d?.status === "completed") { done = true; break; }
                if (d?.status === "failed") { toast.error("La IA no pudo procesar las fotos"); break; }
            }
            if (done) toast.success("Sugerencias IA generadas");
            else if (reportStatus !== "failed") toast.message("Sigue procesando, refresca en unos segundos");
        } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el reporte con IA"); }
        finally { setProcessingPhotos(false); }
    };

    const viewPdf = async () => {
        let url = reportPdfUrl;
        // Old PDFs were saved on ephemeral local disk (/api/uploads) and 404 in prod.
        // Regenerate to durable Object Storage on demand so the link always works.
        if (!url || url.includes("/api/uploads/")) {
            if ((photos?.length || 0) === 0) { toast.error("Aún no hay PDF. Genera las sugerencias con IA primero."); return; }
            setBusy(true);
            try {
                const { data } = await api.post(`/photo-reports/${selected.scheduled_service_id}/regenerate-pdf`);
                url = data.pdf_url;
                setReportPdfUrl(url);
            } catch (e) { toast.error("No se pudo generar el PDF"); return; }
            finally { setBusy(false); }
        }
        window.open(url.startsWith("http") ? url : `${API}${url}`, "_blank");
    };

    const emailReport = async () => {
        if (!photos?.length) { toast.error("El reporte no tiene fotos todavía"); return; }
        const preset = selected?.client_report_email || "";
        const email = window.prompt("Enviar el reporte MIP (PDF adjunto) al correo:", preset);
        if (email === null) return;
        setEmailingReport(true);
        try {
            const { data } = await api.post(`/audits/${selected.id}/email-report`, { email: (email || "").trim() || null });
            toast.success(`Reporte enviado a ${data.email}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo enviar el correo");
        } finally { setEmailingReport(false); }
    };

    const toggleApprove = async () => {
        if (!selected) return;
        const next = !selected.report_approved;
        setBusy(true);
        try {
            await api.post(`/audits/${selected.id}/approve-report?approved=${next}`);
            setSelected((s) => ({ ...s, report_approved: next }));
            setAudits((list) => list.map((x) => x.id === selected.id ? { ...x, report_approved: next } : x));
            if (next) await reloadReport();
            toast.success(next ? "Reporte aprobado — visible para el cliente" : "Aprobación retirada");
        } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar la aprobación"); }
        finally { setBusy(false); }
    };

    // ── Grouped/filtered audits for the active auditor tab ──
    const tabAudits = audits.filter((a) => a.auditor_id === activeAuditor);
    const q = search.trim().toLowerCase();
    const filtered = q
        ? tabAudits.filter((a) =>
            (a.client_name || "").toLowerCase().includes(q) ||
            (a.evaluated_technician_name || "").toLowerCase().includes(q) ||
            (a.audit_date || "").includes(q))
        : tabAudits;
    const groups = {};
    filtered.forEach((a) => { const d = a.audit_date || "Sin fecha"; (groups[d] ||= []).push(a); });
    const sortedDates = Object.keys(groups).sort((a, b) => b.localeCompare(a));

    return (
        <div data-testid="admin-audits-page">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <ClipboardCheck className="w-5 h-5 text-emerald-700" />
                </div>
                <div>
                    <h1 className="text-2xl font-semibold">Auditores</h1>
                    <p className="text-sm text-muted-foreground">Auditorías de calidad organizadas por auditor y fecha</p>
                </div>
            </div>

            {loading ? (
                <div className="space-y-3">{[1, 2, 3].map((i) => <div key={i} className="h-16 rounded-xl bg-secondary animate-pulse" />)}</div>
            ) : auditors.length === 0 ? (
                <Card className="p-8 text-center text-muted-foreground text-sm">Aún no hay auditores ni auditorías registradas.</Card>
            ) : (
                <Tabs value={activeAuditor} onValueChange={(v) => { setActiveAuditor(v); setSelected(null); setSearch(""); }}>
                    <TabsList className="flex flex-wrap h-auto gap-1 bg-secondary/60 p-1" data-testid="auditor-tabs">
                        {auditors.map((au) => {
                            const count = audits.filter((a) => a.auditor_id === au.id).length;
                            return (
                                <TabsTrigger key={au.id} value={au.id} className="data-[state=active]:bg-white gap-1.5" data-testid={`auditor-tab-${au.id}`}>
                                    <UserCircle2 className="w-4 h-4" /> {au.name}
                                    <Badge variant="secondary" className="ml-1 text-[10px] h-4 px-1.5">{count}</Badge>
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>

                    {auditors.map((au) => (
                        <TabsContent key={au.id} value={au.id} className="mt-4">
                            <div className="relative mb-4 max-w-sm">
                                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por cliente, técnico o fecha..." className="pl-9" data-testid="audit-search" />
                            </div>

                            {filtered.length === 0 ? (
                                <Card className="p-8 text-center text-muted-foreground text-sm">{q ? "Sin resultados para la búsqueda." : "Este auditor aún no tiene auditorías."}</Card>
                            ) : (
                                <div className="space-y-5">
                                    {sortedDates.map((date) => (
                                        <div key={date}>
                                            <div className="flex items-center gap-2 mb-2 text-xs font-semibold text-muted-foreground">
                                                <Calendar className="w-3.5 h-3.5" /> {date} <span className="font-normal">({groups[date].length})</span>
                                            </div>
                                            <div className="space-y-2">
                                                {groups[date].map((a) => {
                                                    const p = scorePct(a);
                                                    const open = selected?.id === a.id;
                                                    return (
                                                        <Card key={a.id} className="overflow-hidden" data-testid={`audit-row-${a.id}`}>
                                                            <button onClick={() => toggle(a)} className="w-full flex items-center gap-3 flex-wrap p-3 text-left hover:bg-secondary/40 transition-colors" data-testid={`audit-toggle-${a.id}`}>
                                                                <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="font-medium text-sm flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-muted-foreground" /> {a.client_name || "Cliente"}</div>
                                                                    {a.branch_name && (
                                                                        <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5" data-testid={`audit-branch-${a.id}`}><MapPin className="w-3 h-3" /> Cliente de servicio: {a.branch_name}</div>
                                                                    )}
                                                                    <div className="text-xs text-muted-foreground mt-0.5">Técnico evaluado: {a.evaluated_technician_name || "—"}</div>
                                                                </div>
                                                                <Badge variant="outline" className={`text-[10px] ${a.status === "completed" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>
                                                                    {a.status === "completed" ? "Completada" : "En progreso"}
                                                                </Badge>
                                                                {a.report_approved && (
                                                                    <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-800 border-emerald-200 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Publicado</Badge>
                                                                )}
                                                                {a.client_report_email && (
                                                                    <Badge variant="outline" className="text-[10px] bg-amber-100 text-amber-800 border-amber-200 flex items-center gap-1" title={`Enviar por correo a ${a.client_report_email}`}><Mail className="w-3 h-3" /> Correo</Badge>
                                                                )}
                                                                {a.client_report_whatsapp && (
                                                                    <Badge variant="outline" className="text-[10px] bg-green-100 text-green-800 border-green-200 flex items-center gap-1" title={`Enviar por WhatsApp a ${a.client_report_whatsapp}`}><MessageCircle className="w-3 h-3" /> WhatsApp</Badge>
                                                                )}
                                                                <Badge variant="outline" className={`text-[10px] font-bold ${scoreColor(p)}`}>{p == null ? "Sin puntaje" : `${p}%`}</Badge>
                                                            </button>

                                                            {open && (
                                                                <div className="border-t p-4 bg-secondary/20" data-testid={`audit-detail-${a.id}`}>
                                                                    {renderDetail(a)}
                                                                </div>
                                                            )}
                                                        </Card>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </TabsContent>
                    ))}
                </Tabs>
            )}
        </div>
    );

    // ── Expanded (accordion) detail with sub-tabs ──
    function renderDetail(a) {
        const showChecklist = hasChecklist(a);
        const showPhotos = photos.length > 0;
        const hasNotes = !!a.client_note || serviceNotes.client_notes.length > 0 || serviceNotes.tech_notes.length > 0;
        const showSummary = showChecklist || a.executive_summary || hasNotes;
        return (
            <>
                {/* Client requested email */}
                {a.client_report_email && (
                    <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-3 text-sm" data-testid="audit-client-email-banner">
                        <Mail className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                        <div>
                            <span className="font-semibold text-amber-800">El cliente solicitó recibir este reporte por correo:</span>{" "}
                            <a href={`mailto:${a.client_report_email}`} className="font-medium text-amber-900 underline">{a.client_report_email}</a>
                            <div className="text-[11px] text-amber-700 mt-0.5">Envíelo manualmente una vez aprobado el reporte.</div>
                        </div>
                    </div>
                )}
                {a.client_report_whatsapp && (
                    <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 mb-3 text-sm" data-testid="audit-client-whatsapp-banner">
                        <MessageCircle className="w-4 h-4 text-green-700 mt-0.5 shrink-0" />
                        <div>
                            <span className="font-semibold text-green-800">El cliente solicitó recibir este reporte por WhatsApp:</span>{" "}
                            <a href={`https://wa.me/${a.client_report_whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="font-medium text-green-900 underline">{a.client_report_whatsapp}</a>
                            <div className="text-[11px] text-green-700 mt-0.5">Envíelo manualmente por WhatsApp una vez aprobado el reporte.</div>
                        </div>
                    </div>
                )}

                {(!showSummary && !showChecklist && !showPhotos) ? (
                    <p className="text-sm text-muted-foreground">Esta auditoría aún no tiene checklist ni reporte fotográfico.</p>
                ) : (
                    <Tabs value={subTab} onValueChange={setSubTab} className="w-full">
                        <TabsList className="flex flex-wrap h-auto gap-1 bg-secondary/60 p-1" data-testid="audit-subtabs">
                            {showSummary && <TabsTrigger value="summary" className="data-[state=active]:bg-white text-xs gap-1" data-testid="subtab-summary"><Sparkles className="w-3.5 h-3.5" /> Resumen ejecutivo</TabsTrigger>}
                            {showChecklist && <TabsTrigger value="checklist" className="data-[state=active]:bg-white text-xs gap-1" data-testid="subtab-checklist"><ListChecks className="w-3.5 h-3.5" /> Checklist de supervisión</TabsTrigger>}
                            {showPhotos && <TabsTrigger value="photos" className="data-[state=active]:bg-white text-xs gap-1" data-testid="subtab-photos"><ImageIcon className="w-3.5 h-3.5" /> Reporte fotográfico MIP ({photos.length})</TabsTrigger>}
                        </TabsList>

                        {/* Executive summary */}
                        {showSummary && (
                            <TabsContent value="summary" className="mt-3">
                                <Card className="p-3 bg-blue-50/50 border-blue-100">
                                    <div className="flex items-center justify-between mb-1">
                                        <div className="text-xs font-semibold text-[hsl(var(--trulyn-navy))] flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> Resumen ejecutivo del checklist (IA)</div>
                                        <div className="flex items-center gap-1">
                                            <Button size="sm" variant="ghost" onClick={() => copySummary(a.executive_summary)} disabled={!a.executive_summary} data-testid="copy-summary-btn">
                                                <Copy className="w-3.5 h-3.5" /><span className="ml-1 text-xs">Copiar</span>
                                            </Button>
                                            <Button size="sm" variant="ghost" onClick={regenerateSummary} disabled={regenerating} data-testid="regen-summary-btn">
                                                {regenerating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                                <span className="ml-1 text-xs">Regenerar</span>
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="text-sm whitespace-pre-wrap text-gray-700" data-testid="audit-summary-text">
                                        {a.executive_summary ? cleanSummary(a.executive_summary) : "Sin resumen. Presione 'Regenerar' para generarlo con IA a partir del checklist."}
                                    </div>
                                </Card>

                                {/* NOTAS (recepción + servicio) */}
                                <Card className="p-3 mt-3" data-testid="audit-notes-card">
                                    <div className="text-xs font-semibold text-[hsl(var(--trulyn-navy))] flex items-center gap-1 mb-2">
                                        <MessageCircle className="w-3.5 h-3.5" /> Notas
                                    </div>

                                    {/* Nota del cliente en la firma de recepción */}
                                    {a.client_note && (
                                        <div className="rounded-lg border p-3 mb-3 bg-emerald-50/60" data-testid="audit-receipt-note">
                                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Nota del cliente (recepción / firma)</div>
                                            <p className="text-sm whitespace-pre-wrap">{a.client_note}</p>
                                            {a.accepted_by_name && <div className="text-[10px] text-muted-foreground mt-1.5">— {a.accepted_by_name}</div>}
                                        </div>
                                    )}

                                    {/* Notas del cliente (servicio) */}
                                    <div className="mb-3">
                                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Notas del cliente</h4>
                                        {serviceNotes.client_notes.length === 0 ? (
                                            <p className="text-xs text-muted-foreground italic">Sin notas del cliente</p>
                                        ) : serviceNotes.client_notes.map((n) => (
                                            <div key={n.id} className="rounded-lg border p-3 mb-2 bg-blue-50/50" data-testid={`audit-client-note-${n.id}`}>
                                                <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                                                <div className="flex items-center gap-2 mt-1.5 flex-wrap text-[10px] text-muted-foreground">
                                                    {n.signature_url && <Badge variant="outline" className="text-[10px] bg-green-50 text-green-700 border-green-200 px-1.5 py-0">Firmado</Badge>}
                                                    {n.latitude && (
                                                        <a href={`https://www.google.com/maps?q=${n.latitude},${n.longitude}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">Ubicación</a>
                                                    )}
                                                    {n.created_at && !isNaN(new Date(n.created_at)) && <span className="ml-auto">{new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" })}</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Notas del auditor / técnico (servicio) */}
                                    <div>
                                        <h4 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Notas del auditor</h4>
                                        {serviceNotes.tech_notes.length === 0 ? (
                                            <p className="text-xs text-muted-foreground italic">Sin notas del auditor</p>
                                        ) : serviceNotes.tech_notes.map((n) => (
                                            <div key={n.id} className="rounded-lg border p-3 mb-2 bg-amber-50/50" data-testid={`audit-tech-note-${n.id}`}>
                                                <p className="text-sm whitespace-pre-wrap">{n.notes}</p>
                                                <div className="text-[10px] text-muted-foreground mt-1.5">
                                                    {(n.technician_name ? `${n.technician_name}` : "")}{n.created_at && !isNaN(new Date(n.created_at)) ? `${n.technician_name ? " · " : ""}${new Date(n.created_at).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" })}` : ""}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </Card>
                            </TabsContent>
                        )}

                        {/* Checklist */}
                        {showChecklist && (
                            <TabsContent value="checklist" className="mt-3">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1"><ListChecks className="w-3.5 h-3.5" /> Checklist de supervisión</div>
                                    <div className="flex items-center gap-2">
                                        {a.checklist_score && (
                                            <span className="text-xs text-muted-foreground flex items-center gap-2">
                                                <span className="flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> {a.checklist_score.ok}</span>
                                                <span className="flex items-center gap-1 text-red-600"><XCircle className="w-3.5 h-3.5" /> {a.checklist_score.fail}</span>
                                                {a.checklist_score.na ? <span className="flex items-center gap-1 text-gray-500"><Minus className="w-3.5 h-3.5" /> {a.checklist_score.na}</span> : null}
                                            </span>
                                        )}
                                        <Button size="sm" variant="outline" onClick={() => downloadPdf(`/audits/${a.id}/checklist-pdf`)} disabled={busy} data-testid="download-checklist-btn">
                                            <FileText className="w-3.5 h-3.5 mr-1" /> PDF
                                        </Button>
                                    </div>
                                </div>
                                <ChecklistView template={template} checklist={a.checklist || {}} />
                            </TabsContent>
                        )}

                        {/* Photo report */}
                        {showPhotos && (
                            <TabsContent value="photos" className="mt-3">
                                <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center gap-1">
                                    <ImageIcon className="w-3.5 h-3.5" /> Reporte fotográfico MIP ({photos.length})
                                    {reportStatus === "processing" && <span className="text-amber-600 flex items-center gap-1 ml-2"><Loader2 className="w-3 h-3 animate-spin" /> procesando IA…</span>}
                                </div>
                                <div className="text-xs mb-3 bg-secondary/60 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="audit-receiver-name">
                                    <span className="text-muted-foreground">Recibido por (cliente):</span>
                                    <span className="font-semibold">{a.accepted_by_name || "— sin registro de firma —"}</span>
                                </div>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    <Button size="sm" variant="outline" onClick={generateAI} disabled={processingPhotos} data-testid="gen-photo-report-btn">
                                        {processingPhotos ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                                        {processingPhotos ? "Generando con IA..." : "Generar / Regenerar con IA"}
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={viewPdf} disabled={!reportPdfUrl} data-testid="view-photo-pdf-btn">
                                        <Eye className="w-4 h-4 mr-1" /> Ver PDF
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={emailReport} disabled={emailingReport || !photos?.length} data-testid="email-photo-report-btn">
                                        {emailingReport ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Mail className="w-4 h-4 mr-1" />}
                                        Enviar por correo
                                    </Button>
                                    <Button size="sm" onClick={toggleApprove} disabled={busy}
                                        className={a.report_approved ? "bg-gray-200 text-gray-700 hover:bg-gray-300" : "bg-emerald-600 hover:bg-emerald-700 text-white"}
                                        data-testid="approve-report-btn">
                                        <CheckCircle2 className="w-4 h-4 mr-1" />
                                        {a.report_approved ? "Aprobado ✓ (quitar)" : "Aprobar y publicar al cliente"}
                                    </Button>
                                </div>
                                {a.report_approved && (
                                    <div className="text-xs text-emerald-700 -mt-1 mb-2 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Publicado en el portal del cliente (pestaña "Reportes de auditoría").</div>
                                )}
                                <div className="space-y-3">
                                    {photos.map((ph) => (
                                        <Card key={ph.id} className="p-3" data-testid={`admin-audit-photo-${ph.id}`}>
                                            <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_1fr] gap-3 items-start">
                                                <div>
                                                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Oportunidad de mejora</Label>
                                                    <Textarea rows={6} key={`opp-${ph.id}`} value={editVal(ph, "ai_opportunity")}
                                                        placeholder={reportStatus === "processing" ? "Generando con IA…" : "Genere con IA o escriba aquí"}
                                                        className="text-xs mt-1 resize-none"
                                                        onChange={(e) => setEdit(ph.id, "ai_opportunity", e.target.value)}
                                                        data-testid={`admin-photo-opp-${ph.id}`} />
                                                </div>
                                                <div>
                                                    <img src={`${API}${ph.photo_url}`} alt={ph.area} className="w-full h-32 object-cover rounded border" />
                                                    <div className="text-[10px] text-muted-foreground mt-1">Zona {ph.zone} · {ph.custom_area || ph.area}</div>
                                                    {ph.description && <div className="text-[10px] text-muted-foreground italic mt-0.5 line-clamp-2">Nota: {ph.description}</div>}
                                                </div>
                                                <div>
                                                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">Acción correctiva sugerida</Label>
                                                    <Textarea rows={6} key={`act-${ph.id}`} value={editVal(ph, "ai_corrective_action")}
                                                        placeholder={reportStatus === "processing" ? "Generando con IA…" : "Genere con IA o escriba aquí"}
                                                        className="text-xs mt-1 resize-none"
                                                        onChange={(e) => setEdit(ph.id, "ai_corrective_action", e.target.value)}
                                                        data-testid={`admin-photo-action-${ph.id}`} />
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                                                <Switch checked={!!ph.is_reincident} onCheckedChange={(v) => savePhotoField(ph.id, { is_reincident: v })} data-testid={`admin-photo-reincident-${ph.id}`} />
                                                <Label className="text-xs">Reincidente</Label>
                                                <div className="ml-auto flex items-center gap-2">
                                                    {isDirty(ph) && <span className="text-[11px] text-amber-600">Cambios sin guardar</span>}
                                                    <Button size="sm" onClick={() => savePhotoTexts(ph)} disabled={!isDirty(ph) || savingPhotoId === ph.id} data-testid={`admin-photo-save-${ph.id}`}>
                                                        {savingPhotoId === ph.id ? "Guardando…" : (isDirty(ph) ? "Guardar" : "Guardado ✓")}
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            </TabsContent>
                        )}
                    </Tabs>
                )}
            </>
        );
    }
}
